import { Component, Input } from '@angular/core';
import {
    RpaDto,
    RegisterEntryOnMissionSummary,
    RpaCategoryGroup,
    RpaTypeDto
} from '@flyfreely-portal-ui/flyfreely';
import {
    FormatRpaPipe,
    FormatRpaTypePipe
} from '@flyfreely-portal-ui/resource-ui';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MissionEditService } from '../mission-edit.service';

/**
 * An inline component that display the mission specific register details per pilot.
 * Only accepts one pilot ID and displays register details relevant to that pilot.
 * This is shown in a table with one row for each RPA type and shows the RPA in that type and the pilot's status for that type on the current mission register.
 */
@Component({
    selector: 'pilot-register-details',
    templateUrl: './pilot-register-details.component.html'
})
export class PilotMissionRegisterDetails {
    @Input() pilotId: number;
    @Input() rowEven: boolean;
    @Input() primaryAuthorityPilotRegisterDisabled: boolean;
    @Input() authorityIndicatorLookup: { [authorityId: number]: string };

    entrySummaries: RegisterEntryOnMissionSummary[];
    statusSource: RpaCategoryGroup.RegisterStatusSource | 'NONE' = 'NONE';
    rpaTypes: { [rpaId: number]: string } = {};
    rpas: { [rpaId: number]: string } = {};
    rpaIds: number[];
    authorityIds: number[];
    registerStatuses: {
        [rpaId: number]: {
            [authorityId: number]: {
                registerStatus: RpaCategoryGroup.RegisterStatus;
                registerStatusSource: RpaCategoryGroup.RegisterStatusSource;
            };
        };
    };

    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private missionEditService: MissionEditService,
        private formatRpaTypePipe: FormatRpaTypePipe,
        private formatRpaPipe: FormatRpaPipe
    ) {}

    ngOnInit() {
        combineLatest([
            this.missionEditService.registerEntriesOnMission$.pipe(
                filter(e => e != null)
            ),
            this.missionEditService.rpaTypes$.pipe(filter(e => e != null)),
            this.missionEditService.rpas$.pipe(filter(e => e != null))
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([entries, rpaTypes, rpas]) => {
                this.entrySummaries = entries.filter(
                    e => e.entityId === this.pilotId
                );
                this.rpaIds = this.entrySummaries.reduce((acc, entry) => {
                    const rpaLists =
                        entry.registerStatusGroupList != null
                            ? entry.registerStatusGroupList.reduce(
                                  (acc2, list) =>
                                      acc.concat(
                                          (list.rpaIdList ?? []).filter(
                                              id => !acc2.includes(id)
                                          )
                                      ),
                                  []
                              )
                            : [];
                    return acc.concat(
                        rpaLists.filter(rpaId => !acc.includes(rpaId))
                    );
                }, []);

                this.authorityIds = entries.map(e => e.authorityId);

                this.registerStatuses = this.rpaIds.reduce((acc, id) => {
                    const relativeRegisters = this.entrySummaries.filter(
                        e =>
                            e.registerStatusGroupList != null &&
                            e.registerStatusGroupList.find(
                                list =>
                                    list.rpaIdList != null &&
                                    list.rpaIdList.includes(id)
                            ) != null
                    );
                    const findStatusValues = (
                        registerStatusGroupList: RpaCategoryGroup[]
                    ) => {
                        const statusGroup = registerStatusGroupList.find(
                            group =>
                                group.rpaIdList != null &&
                                group.rpaIdList.includes(id)
                        );
                        return {
                            registerStatus: statusGroup.registerStatus,
                            registerStatusSource:
                                statusGroup.registerStatusSource
                        };
                    };
                    const authorityIdObject = relativeRegisters.reduce(
                        (acc2, register) => ({
                            ...acc2,
                            [register.authorityId]: findStatusValues(
                                register.registerStatusGroupList
                            )
                        }),
                        {}
                    );
                    return {
                        ...acc,
                        [id]: authorityIdObject
                    };
                }, {});

                if (this.rpaIds != null && this.rpaIds.length > 0) {
                    this.parseRpaLists(rpaTypes, rpas);
                }

                // Find the overall status source across all authorities.
                // If there is more than one authority, return AUTO.
                // If there aren't any, return NONE.
                // Otherwise, return the single authority's primary status.
                const registerStatusGroups =
                    this.entrySummaries != null
                        ? this.entrySummaries
                              .map(summary => summary.registerStatusGroupList)
                              .filter(group => group != null)
                        : [];
                this.statusSource =
                    registerStatusGroups.length > 1
                        ? 'AUTO'
                        : registerStatusGroups.length > 0
                        ? registerStatusGroups[0][0].registerStatusSource
                        : 'NONE';
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    /**
     * A function to build lookups and other objects for the HTML to use
     * Reduces down the larger RPATypes array into only the types related to the RPA on the mission
     * Aims to prevent excessive logic and function calling in the HTML by pre-parsing all data for display
     * @param entry The register entry with details for this pilot
     * @param rpaTypes The full organisation RPA types list
     * @param rpas The RPAs on the current mission (from the mission edit service)
     */
    parseRpaLists(rpaTypes: RpaTypeDto[], rpas: RpaDto[]) {
        this.rpaTypes = this.rpaIds.reduce(
            (acc, id) => ({
                ...acc,
                [id]: this.formatRpaTypePipe.transform(
                    rpaTypes.find(
                        t => t.id === rpas.find(rpa => rpa.id === id)?.rpaTypeId
                    ),
                    'Unknown'
                )
            }),
            {}
        );
        this.rpas = this.rpaIds.reduce(
            (acc, id) => ({
                ...acc,
                [id]:
                    this.formatRpaPipe.transform(
                        rpas.find(rpa => rpa.id === id)
                    ) ?? ''
            }),
            {}
        );
    }
}
